import { Activity, CalendarCheck2, FlaskConical, Pill, ShieldAlert } from 'lucide-react'
import { cn } from '@lib/utils'
import { useClinicFormat } from '@context/ClinicTimezoneContext'
import type { DashboardActivity } from '@hooks/useDashboardData'

const ICON: Record<DashboardActivity['type'], React.ElementType> = {
  appointment: CalendarCheck2,
  lab: FlaskConical,
  prescription: Pill,
  safety: ShieldAlert,
}
const DOT: Record<DashboardActivity['status'], string> = {
  success: 'bg-emerald-500',
  pending: 'bg-amber-400',
  critical: 'bg-ml-error',
}

const STATUS_ES: Record<string, string> = {
  pending_approval: 'por confirmar', scheduled: 'programada', confirmed: 'confirmada', checked_in: 'en sala',
  in_progress: 'en consulta', completed: 'atendida', cancelled: 'cancelada', rejected: 'rechazada', no_show: 'no asistió',
}
const humanize = (title: string) =>
  title
    .replace(/^Appointment\b/i, 'Cita')
    .replace(/\b(pending_approval|scheduled|confirmed|checked_in|in_progress|completed|cancelled|rejected|no_show)\b/g, (m) => STATUS_ES[m] ?? m.replace(/_/g, ' '))

export function ActivityFeed({ items, isLoading }: { items: DashboardActivity[]; isLoading: boolean }) {
  const { formatDateTime } = useClinicFormat()
  const visible = items.filter((i) => i.type === 'appointment' || i.type === 'safety').slice(0, 6)
  return (
    <section className="flex min-w-0 flex-col rounded-2xl border border-ml-outline-variant/15 bg-ml-surface-container-lowest">
      <header className="flex items-center gap-3 border-b border-ml-outline-variant/10 px-5 py-4 sm:px-6">
        <span className="grid h-9 w-9 place-items-center rounded-xl bg-ml-surface-container-low text-ml-primary"><Activity className="h-4 w-4" /></span>
        <div>
          <h2 className="font-headline text-2xl leading-tight text-ml-primary">Actividad reciente</h2>
          <p className="text-xs text-ml-on-surface-variant/70">Movimientos del consultorio</p>
        </div>
      </header>
      {isLoading ? (
        <div className="space-y-2 p-5">{[0, 1, 2].map((i) => <div key={i} className="h-10 animate-pulse rounded-lg bg-ml-surface-container-low" />)}</div>
      ) : visible.length === 0 ? (
        <p className="px-6 py-10 text-center text-sm text-ml-on-surface-variant/70">Sin movimientos recientes.</p>
      ) : (
        <ul className="divide-y divide-ml-outline-variant/10 px-2 py-1 sm:px-3">
          {visible.map((it) => {
            const Icon = ICON[it.type] ?? Activity
            return (
              <li key={it.id} className="grid grid-cols-[auto_1fr_auto] items-center gap-3 px-2 py-3">
                <span className="relative grid h-8 w-8 place-items-center rounded-lg bg-ml-surface-container-low text-ml-primary">
                  <Icon className="h-4 w-4" />
                  <span className={cn('absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full ring-2 ring-ml-surface-container-lowest', DOT[it.status])} />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-ml-on-surface">{humanize(it.title)}</p>
                  <p className="truncate text-xs text-ml-on-surface-variant/75">{it.subtitle}</p>
                </div>
                <span className="text-[11px] tabular-nums text-ml-on-surface-variant/60">{formatDateTime(it.timestamp)}</span>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
